import React, { useState } from 'react';
import { storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const AddProductModal = ({ isOpen, onClose, onAddProduct }) => {
    const [id, setId] = useState('');
    const [name, setName] = useState('');
    const [nameAr, setNameAr] = useState('');
    const [price, setPrice] = useState('');
    const [salePrice, setSalePrice] = useState('');
    const [category, setCategory] = useState('');
    const [description, setDescription] = useState('');
    const [descriptionAr, setDescriptionAr] = useState('');
    const [quantity, setQuantity] = useState('');
    const [isNew, setIsNew] = useState(false);
    
    // Image Upload State
    const [imageFile, setImageFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!id || !name || !price || !imageFile) {
            alert("Please fill in all required fields and select an image.");
            return;
        }

        setIsUploading(true);

        try {
            // 1. Upload Image to Firebase Storage
            const storageRef = ref(storage, `products/${Date.now()}_${imageFile.name}`);
            const snapshot = await uploadBytes(storageRef, imageFile);
            const downloadURL = await getDownloadURL(snapshot.ref);

            // 2. Prepare Product Data
            const productData = {
                id,
                name,
                nameAr: nameAr.trim(),
                price: Number(price),
                salePrice: salePrice ? Number(salePrice) : null,
                category,
                description,
                descriptionAr: descriptionAr.trim(),
                quantity: Number(quantity) || 0,
                isNew,
                imageUrls: [downloadURL] 
            };

            // 3. Save to Firestore (via App.js handler)
            await onAddProduct(productData);
            
            // 4. Reset & Close
            handleClose();

        } catch (error) {
            console.error("Error uploading image:", error);
            alert("Failed to upload image. Please try again.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleClose = () => {
        setId('');
        setName('');
        setNameAr('');
        setPrice('');
        setSalePrice('');
        setCategory('');
        setDescription('');
        setDescriptionAr('');
        setQuantity('');
        setIsNew(false);
        setImageFile(null);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={handleClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center p-6 border-b">
                    <h2 className="text-2xl font-bold text-gray-800">Add New Product</h2>
                    <button onClick={handleClose} className="text-gray-500 hover:text-gray-700 text-2xl" aria-label="Close">&times;</button>
                </div>
                
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Product ID / SKU *</label>
                            <input type="text" required value={id} onChange={e => setId(e.target.value)} className="w-full p-2 border rounded focus:ring-[#b08d57]" placeholder="e.g. VASE-001" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Product Name (English) *</label>
                            <input type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full p-2 border rounded focus:ring-[#b08d57]" placeholder="e.g. Ceramic Vase" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Product Name in Arabic / اسم المنتج بالعربية (Optional)</label>
                            <input type="text" dir="rtl" value={nameAr} onChange={e => setNameAr(e.target.value)} className="w-full p-2 border rounded focus:ring-[#b08d57]" placeholder="مثال: فازة سيراميك يدوية" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Price (EGP) *</label>
                            <input type="number" required value={price} onChange={e => setPrice(e.target.value)} className="w-full p-2 border rounded focus:ring-[#b08d57]" placeholder="0.00" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Sale Price (Optional)</label>
                            <input type="number" value={salePrice} onChange={e => setSalePrice(e.target.value)} className="w-full p-2 border rounded focus:ring-[#b08d57]" placeholder="0.00" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                            <input type="text" value={category} onChange={e => setCategory(e.target.value)} className="w-full p-2 border rounded focus:ring-[#b08d57]" placeholder="e.g. Vases" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Stock Quantity</label>
                            <input type="number" value={quantity} onChange={e => setQuantity(e.target.value)} className="w-full p-2 border rounded focus:ring-[#b08d57]" placeholder="10" />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description (English)</label>
                        <textarea value={description} onChange={e => setDescription(e.target.value)} rows="3" className="w-full p-2 border rounded focus:ring-[#b08d57]"></textarea>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description in Arabic / وصف المنتج بالعربية (Optional)</label>
                        <textarea dir="rtl" value={descriptionAr} onChange={e => setDescriptionAr(e.target.value)} rows="3" className="w-full p-2 border rounded focus:ring-[#b08d57]" placeholder="وصف تفصيلي للمنتج باللغة العربية..."></textarea>
                    </div>

                    {/* Image Upload Section */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Product Image *</label>
                        <input 
                            type="file" 
                            accept="image/*"
                            onChange={(e) => setImageFile(e.target.files[0])}
                            className="w-full p-2 border border-gray-300 rounded text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#b08d57] file:text-white hover:file:bg-[#9c7b4d]"
                        />
                        {imageFile && (
                            <p className="mt-2 text-sm text-gray-500">Selected: {imageFile.name}</p>
                        )}
                    </div>

                    <div className="flex items-center">
                        <input type="checkbox" id="isNew" checked={isNew} onChange={e => setIsNew(e.target.checked)} className="h-4 w-4 text-[#8B6939] focus:ring-[#b08d57] border-gray-300 rounded" />
                        <label htmlFor="isNew" className="ml-2 block text-sm text-gray-900">Mark as New Arrival</label>
                    </div>

                    <div className="pt-4 border-t flex justify-end gap-3">
                        <button type="button" onClick={handleClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md">Cancel</button>
                        <button 
                            type="submit" 
                            disabled={isUploading}
                            className="px-6 py-2 bg-[#b08d57] text-white font-bold rounded-md hover:bg-[#9c7b4d] disabled:opacity-50 flex items-center"
                        >
                            {isUploading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                    Uploading...
                                </>
                            ) : 'Add Product'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddProductModal;
